import { OptionsMenuStorybook } from "@finanedi.es/storybook"
import {
  faCheck,
  faFile,
  faPen,
  faTrashAlt,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FlujoAprobacionReadingDtoProps } from "api/DTOs/FlujoAprobacionReadingDto"
import { RangoImporteDtoProps } from "api/DTOs/RangoImporteDto"
import {
  OPERADOR_RANGO_FLUJO_OPERACION,
  RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM,
} from "api/Types/RangoFlujoOperacionOperadorLogicoEnum"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { frontendCurrency } from "utils/formatCurrency"
import { TIPO_DOC_LABEL } from "../constants/tipo-doc-label"
import { ScrollableDoubleTimeline } from "./ScrollableDoubleTimeline"

const operadorToStr = (op: number) => {
  switch (op) {
    case RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.EQ:
      return OPERADOR_RANGO_FLUJO_OPERACION[
        RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.EQ
      ]
    case RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.GT:
      return OPERADOR_RANGO_FLUJO_OPERACION[
        RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.GT
      ]
    case RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.LT:
      return OPERADOR_RANGO_FLUJO_OPERACION[
        RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.LT
      ]
    case RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.GE:
      return OPERADOR_RANGO_FLUJO_OPERACION[
        RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.GE
      ]
    case RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.LE:
      return OPERADOR_RANGO_FLUJO_OPERACION[
        RANGO_FLUJO_OPERACION_OPERADOR_LOGICO_ENUM.LE
      ]
    default:
      return ""
  }
}

const rangoToTitle = (tipo: number, t: TFunction, r?: RangoImporteDtoProps) => {
  if (!r) return TIPO_DOC_LABEL(t)[tipo]
  const base = TIPO_DOC_LABEL(t)[tipo]
  const { operador, valorInferior, valorSuperior } = r
  if (valorInferior != null && valorSuperior != null) {
    return `${base} ${frontendCurrency(valorInferior)} - ${frontendCurrency(
      valorSuperior
    )}`
  }
  if (valorInferior != null && operador != null) {
    return `${base} ${operadorToStr(operador)} ${frontendCurrency(
      valorInferior
    )}`
  }
  return base
}

export interface ReglaCardProps extends FlujoAprobacionReadingDtoProps {
  onDelete: () => void
  onEdit: () => void
}

export const ReglaCard: React.FC<{
  tipo: number
  regla: ReglaCardProps
  id: string
}> = ({ tipo, regla, id }) => {
  const { t } = useTranslation()

  return (
    <div className='tw-border tw-rounded-md tw-bg-white tw-p-4 tw-w-full'>
      <div className='tw-flex tw-items-start tw-justify-between tw-gap-4'>
        <div className='tw-flex tw-items-center tw-gap-2'>
          <FontAwesomeIcon
            icon={faFile}
            className='tw-text-lg tw-text-secondary tw-size-5'
          />
          <div>
            <div className='tw-text-base tw-font-medium'>
              {rangoToTitle(tipo, t, regla?.rango)}
            </div>
          </div>
        </div>
        <OptionsMenuStorybook
          id={`regla-card-options-${id}`}
          buttonClassName='tw-border-none'
          items={[
            {
              hasRedirect: false,
              action: regla.onEdit,
              icon: faPen,
              title: t("flujoAprobaciones.items.editFlow"),
            },
            {
              hasRedirect: false,
              action: regla.onDelete,
              icon: faTrashAlt,
              title: t("flujoAprobaciones.items.deleteFlow"),
              className: "tw-text-red-600",
            },
          ]}
          position='right'
        />
      </div>

      {!regla.autovalidacion ? (
        <ScrollableDoubleTimeline
          items={
            regla?.etapas?.map((etapa, i) => {
              const roles =
                etapa.numeroFirmas === 1
                  ? [t("flujoAprobaciones.types.responsibleLabel")]
                  : [
                      `${t("flujoAprobaciones.types.employeeLabel")}${
                        Number(etapa.numeroFirmas) - 1 > 1
                          ? `s (x${Number(etapa.numeroFirmas) - 1})`
                          : ""
                      }`,
                      `${t("flujoAprobaciones.types.responsibleLabel")}`,
                    ]

              return {
                code: etapa.departamentoNombre?.slice(0, 2).toUpperCase() ?? "",
                title: etapa.departamentoNombre ?? "",
                roles,
                id: i,
              }
            }) || []
          }
        />
      ) : (
        <div className='tw-flex tw-flex-col tw-mx-6 tw-text-sm tw-w-fit tw-min-w-[240px]'>
          <FontAwesomeIcon
            icon={faCheck}
            className='tw-flex tw-text-white tw-text-2xl tw-mx-auto tw-mt-2 tw-bg-secondary tw-rounded-full tw-py-1.5 tw-px-2'
          />
          <h3
            className='tw-font-medium tw-text-gray-900 tw-text-sm tw-uppercase tw-text-center tw-h-8 tw-mt-4'
            title='Autovalidación'
          >
            {t("flujoAprobaciones.validation.title")}
          </h3>
          <span className='tw-flex tw-gap-2 tw-mx-auto'>
            <input
              type='radio'
              readOnly
              checked={false}
              className='tw-pointer-events-none tw-my-auto'
            />
            <p className='tw-text-sm tw-pt-0.5'>
              {t("flujoAprobaciones.validation.label")}
            </p>
          </span>
        </div>
      )}
    </div>
  )
}
